import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import TeamUsersHeader from "@/components/molecules/table-headers/TeamUsersHeader";
import Table from "@/components/molecules/table/Table";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { splitText } from "@/helpers/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import { TeamUser, TeamUserFilters } from "@/interfaces/TeamUser";
import { useGetTeamQuery } from "@/services/teams";
import {
  useDestroyTeamUserMutation,
  useGetTeamUsersQuery,
} from "@/services/teams/users";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useMemo, useRef } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const TeamDetailsCard: FC<{ id: string }> = ({ id }) => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const user = useSelector(selectCurrentUser);
  const isAdmin = checkIfUserHasRoles(user, ["ADMIN"]);
  const [deleteTeamUserAsync] = useDestroyTeamUserMutation();

  const { data: team, isLoading, isError, error } = useGetTeamQuery(id);

  const [state, setState] = useTableState<
    Partial<TableState> & TeamUserFilters
  >(`teamUsers-${id}`, {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data: teamUsersData } = useGetTeamUsersQuery([
    id,
    {
      ...state.pagination,
      query: debouncedQuery,
      ...(state.sorting?.[0] && {
        sortBy: state.sorting[0].id,
        sortDirection: state.sorting[0].desc ? "desc" : "asc",
      }),
    },
  ]);

  const { data: managersData } = useGetTeamUsersQuery([
    id,
    { pageIndex: 0, pageSize: 200, isManager: true },
  ]);

  const isCurrentUserManager = useMemo(
    () => managersData?.data?.some((m) => m.userId === user?.id),
    [managersData, user?.id],
  );

  const onDelete = async (userId: string) => {
    toast.promise(deleteTeamUserAsync([id, userId]).unwrap(), {
      pending: "Suppression du membre en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return <p className="mb-0 text-center">Membre supprimé</p>;
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const columns = useRef<ColumnDef<TeamUser>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ userId, isManager: rowIsManager }) => (
        <Fragment>
          {(isAdmin || isCurrentUserManager) && !rowIsManager && (
            <Button
              variant="danger"
              size="sm"
              className="ms-2"
              onClick={() =>
                DeleteModalRef.current?.showOrHiddenModal(userId)
              }
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        </Fragment>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ userId }) => splitText(userId, 10),
      id: "userId",
      cell: (info) => info.getValue(),
    },
    {
      header: "Rôle",
      footer: (props) => props.column.id,
      accessorFn: ({ isManager }) => (
        <SoftBadge bg={isManager ? "success" : "secondary"}>
          {isManager ? "Manager" : "Membre"}
        </SoftBadge>
      ),
      id: "isManager",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ user }) => `${user.firstname} ${user.lastname}`,
      id: "fullname",
      cell: (info) => info.getValue(),
    },
    {
      header: "Email",
      footer: (props) => props.column.id,
      accessorFn: ({ user }) => user.email,
      id: "email",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimer ce membre : {{id}} ?"
      />
      <Card className="mb-3">
        <CardHeader title="Informations" />
        <Card.Body>
          <Row className="gy-2">
            <Col md={6}>
              <b>Nom :</b> {team?.name}
            </Col>
            <Col md={6}>
              <b>Couleur :</b>{" "}
              {team?.color ? (
                <SoftBadge bg={team.color} textColor="#fff">
                  {team.color}
                </SoftBadge>
              ) : (
                "Aucune"
              )}
            </Col>
            <Col md={12}>
              <b>Description :</b> {team?.description || "Aucune"}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Table
        resource="team-users"
        tableName={`teamUsers-${id}`}
        HeaderComponent={(props) => (
          <TeamUsersHeader
            {...props}
            teamId={id}
            canAdd={isAdmin || !!isCurrentUserManager}
          />
        )}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={teamUsersData?.data}
        meta={teamUsersData?.meta}
      />
    </div>
  );
};

export default TeamDetailsCard;
